/** @format */
import { ChangeDetectionStrategy, Component, inject, Input, ViewEncapsulation } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { LocaleService } from 'sis-common';
import { ComboboxOption } from '../../../select/combobox/combobox.component';
import { CommonCodeService, GroupedCodes } from '../../../service/common-code.service';

function alphabeticalSort(a: ComboboxOption, b: ComboboxOption) {
    return (a?.label ?? '').localeCompare(b?.label ?? '');
}

@Component({
    selector: 'sis-search-filter-code-book',
    templateUrl: './search-filter-code-book.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class SearchFilterCodeBookComponent {
    @Input({ required: true })
    set codeBookUrn(urn: string) {
        this.options$ = !urn
            ? EMPTY
            : this.codeBookService.getGroupedCodesObservable(urn).pipe(
                  map(codes => this.toOptions(codes)),
                  catchError(e => {
                      console.error(`Error fetching code book values with URN '${urn}'`, e);
                      return of([]);
                  }),
              );
    }

    @Input({ required: true }) key: string;

    @Input({ required: true }) label: string;

    @Input({ required: false }) optionValueRegexFilter: string;

    options$: Observable<ComboboxOption[]>;

    private readonly codeBookService = inject(CommonCodeService);
    private readonly localeService = inject(LocaleService);
    private readonly transloco = inject(TranslocoService);

    private toOptions(codes: GroupedCodes): ComboboxOption[] {
        const universityCodeOptions = this.filterOptions(
            Object.values(codes.universityCodes ?? {})
                .map(code => ({ label: this.localeService.localize(code.name), value: code.urn }))
                .sort(alphabeticalSort),
        );

        const otherCodeOptions = this.filterOptions(
            Object.values(codes.otherCodes ?? {})
                .map(code => ({ label: this.localeService.localize(code.name), value: code.urn }))
                .sort(alphabeticalSort),
        );

        if (universityCodeOptions.length > 0 && otherCodeOptions.length > 0) {
            return [
                {
                    label: this.transloco.translate('SIS_COMPONENTS.SELECT.MY_UNIVERSITY'),
                    value: 'MY_UNIVERSITY',
                    header: true,
                },
                ...universityCodeOptions,
                {
                    label: this.transloco.translate('SIS_COMPONENTS.SELECT.OTHER_UNIVERSITIES'),
                    value: 'OTHER_UNIVERSITIES',
                    header: true,
                },
                ...otherCodeOptions,
            ];
        }

        return [...universityCodeOptions, ...otherCodeOptions];
    }

    private filterOptions(options: ComboboxOption[]): ComboboxOption[] {
        if (!this.optionValueRegexFilter) {
            return options;
        }
        const regex = new RegExp(this.optionValueRegexFilter);
        return options.filter(option => regex.test(option.value));
    }
}
